import { type FC, useContext, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { PageContainer } from "@packsize/components";
import { Grid, Stack, Typography } from "@mui/material";

import { readLocalStorage, writeLocalStorage } from "@/api/local-storage";
import { ProductionContext } from "@/contexts/production";
import { MachineGroupContext } from "@/contexts/machine-group";
import { sortObjectsAscending } from "@/utils/sort";
import { type MachineGroup } from "@/schemas/machine-group";
import { type ProductionGroup } from "@/schemas";

import { ProductionGroupCard } from "./components/production-group-card";
import { MachineGroupCard } from "./components/machine-group-card";

const MACHINE_GROUP_FAVORITES_KEY = "MachineGroupFavorites";

const getStoredFavorites = (): string[] => {
	try {
		const stored = readLocalStorage(MACHINE_GROUP_FAVORITES_KEY);
		if (!stored) return [];
		return JSON.parse(stored) as string[];
	} catch {
		return [];
	}
};

export interface SelectMachineGroupProps {
	readonly displayHeader?: boolean;
	readonly isPGSelectionEnabled?: boolean;
}

export const SelectMachineGroup: FC<SelectMachineGroupProps> = ({
	displayHeader = true,
	isPGSelectionEnabled = false,
}) => {
	const [favorites, setFavorites] = useState<string[]>(getStoredFavorites);
	const { machineGroups, productionGroups, setCurrentProductionGroup } = useContext(ProductionContext);
	const { changeMachineGroup } = useContext(MachineGroupContext);
	const [sortedMachineGroups, setSortedMachineGroups] = useState<MachineGroup[]>([]);

	const handleSelectProductionGroup = (productionGroup: ProductionGroup) => {
		setCurrentProductionGroup(productionGroup);
	};

	useEffect(() => {
		const favorited = machineGroups.filter((mg) => favorites?.includes(mg.id));
		const notFavorited = machineGroups.filter((mg) => !favorites?.includes(mg.id));
		const sortedFavorited = sortObjectsAscending(favorited, "alias");
		const sortedNotFavorited = sortObjectsAscending(notFavorited, "alias");
		const sorted = sortedFavorited.concat(sortedNotFavorited);

		setSortedMachineGroups(sorted);
	}, [machineGroups, favorites]);

	const handleMachineGroupClick = (machineGroup: MachineGroup): void => {
		changeMachineGroup(machineGroup);
	};

	const handleMachineGroupFavoriteClick = (machineGroup: MachineGroup): void => {
		const newFavorites = favorites.includes(machineGroup.id)
			? favorites.filter((id) => id !== machineGroup.id)
			: [...favorites, machineGroup.id];

		writeLocalStorage(MACHINE_GROUP_FAVORITES_KEY, JSON.stringify(newFavorites));
		setFavorites(newFavorites);
	};

	const { pgStatus, sortedProductionGroups } = useMemo(() => {
		if (!productionGroups || !machineGroups || !isPGSelectionEnabled)
			return { pgStatus: {}, sortedProductionGroups: [] };

		const pgStatus = productionGroups.reduce(
			(acc, pg) => {
				const matchingMg = machineGroups.filter((mg) => pg.configuredMachineGroups.includes(mg.id));
				const onlineMachineGroups = matchingMg.reduce(
					(acc, mg) => (mg.status.toLowerCase() === "online" ? acc + 1 : acc),
					0,
				);
				acc[pg.id] = onlineMachineGroups;
				return acc;
			},
			{} as Record<string, number>,
		);

		return { pgStatus, sortedProductionGroups: sortObjectsAscending(productionGroups, "alias") };
	}, [machineGroups, productionGroups, isPGSelectionEnabled]);

	return (
		<PageContainer>
			<Stack spacing={2}>
				{displayHeader === true && (
					<Typography variant="h1">
						{isPGSelectionEnabled ? (
							<FormattedMessage id="Select A Location" defaultMessage="Select A Location" />
						) : (
							<FormattedMessage id="Select A Machine Group" defaultMessage="Select A Machine Group" />
						)}
					</Typography>
				)}
				{isPGSelectionEnabled === true && sortedMachineGroups.length > 0 && (
					<Typography variant="body1">
						<FormattedMessage id="Machine Groups" defaultMessage="Machine Groups" />
					</Typography>
				)}

				<Grid container display="grid" gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))" gap={2}>
					{sortedMachineGroups.map((machineGroup) => (
						<MachineGroupCard
							key={machineGroup.id}
							machineGroup={machineGroup}
							onClick={() => handleMachineGroupClick(machineGroup)}
							onFavoriteClick={() => handleMachineGroupFavoriteClick(machineGroup)}
							isFavorite={favorites.includes(machineGroup.id)}
						/>
					))}
				</Grid>

				{isPGSelectionEnabled === true && (
					<>
						{sortedMachineGroups.length > 0 && (
							<Typography variant="body1">
								<FormattedMessage id="Production Groups" defaultMessage="Production Groups" />
							</Typography>
						)}
						<Grid container display="grid" gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))" gap={2}>
							{sortedProductionGroups.map((productionGroup) => (
								<ProductionGroupCard
									key={productionGroup.id}
									productionGroup={productionGroup}
									onClick={() => handleSelectProductionGroup(productionGroup)}
									machineGroupsOnline={pgStatus[productionGroup.id] ?? 0}
								/>
							))}
						</Grid>
					</>
				)}
			</Stack>
		</PageContainer>
	);
};
